import React, { useState, useEffect, useRef, useCallback } from 'react';
import Layout from '../component/layout'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from 'xlsx';
import { Tag } from 'primereact/tag';
import { getItem } from "../../../utils/localStorage";
import { makeRequest2 } from "../../../utils/makeRequest";
import { toastify } from "../../../utils/toast";
import { getUnitUrl, getUnitLeaseIncomeURL, statementOfAccountsURL } from "../../../utils/urls";

const ViewUnit = () => {
    const navigate = useNavigate()
    const { unitId } = useParams();
    const [unit, setUnit] = useState({});
    const [tenant, setTenant] = useState({});
    const [homeOwner, setHomeOwner] = useState({});
    const [unitName, setUnitName] = useState('');
    const [unitType, setUnitType] = useState('')
    const [floorUnit, setFloorUnit] = useState('');
    const [viewIncomeDialog, setViewIncomeDialog] = useState(false);
    const [leaseIncome, setLeaseIncome] = useState(0);
    const [invoice, setInvoice] = useState(null);

    const [tenantId, setTenantId] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [agingData, setAgingData] = useState([]);
    const [statementSummary, setStatementSummary] = useState(null);


    const getUnit = async () => {
        try {
            const facilityId = await getItem('selectedFacilityId');
            const response = await makeRequest2(getUnitUrl + '/' + facilityId + '/' + unitId, 'GET')

            if (response.success === true) {
                const unit = response.data;
                setUnit(unit)
                setUnitName(unit.name);
                setUnitType(unit.unitType);
                setFloorUnit(unit.floorUnitNo);

                setHomeOwner(unit.homeOwner || null);
                setTenant(unit.tenant || null);

                // Extract tenant ID correctly from unit object
                // Make sure it's a string, not an ObjectId
                if (unit.tenantId) {
                    const tid = typeof unit.tenantId === 'object' ? unit.tenantId.toString() : unit.tenantId;
                    setTenantId(tid);
                }

                // Fetch lease income for this unit
                fetchLeaseIncome(facilityId, unitId);

                // Only fetch statement of accounts after tenant ID is available
                if (unit.tenantId) {
                    const tid = typeof unit.tenantId === 'object' ? unit.tenantId.toString() : unit.tenantId;
                    fetchStatementOfAccounts(facilityId, tid);
                }

            }
            else {
                throw new Error(response.error)
            }
        }
        catch (err) {
            toastify(err.message, 'error')
        }
    }

    const fetchLeaseIncome = async () => {
        try {
            const facilityId = await getItem('selectedFacilityId');

            const response = await makeRequest2(`${getUnitLeaseIncomeURL}/${facilityId}/${unitId}`, 'GET');

            if (response.success) {
                // ✅ Set the entire response.data instead of just totalIncome
                setLeaseIncome(response.data);
            } else {
                throw new Error(response.error);
            }
        } catch (err) {
            toastify(err.message, 'error');
        }
    };

    const fetchStatementOfAccounts = async (facilityId, tenantId) => {
        try {
            const homeOwnerId = await getItem('selectedCustomerId');

            const response = await makeRequest2(
                `${statementOfAccountsURL}/${facilityId}/${tenantId}/${homeOwnerId}/${unitId}`,
                "GET",
                {}
            );
            console.log("Statement Response:", response);
            if (response.success) {
                setTransactions(response.data.transactions);
                setStatementSummary(response.data.summary);
            } else {
                throw new Error(response.error);
            }
        } catch (err) {
            toastify(err.message, "error");
        }
    };

    const calculateTotals = (transactions) => {
        const totals = transactions.reduce((acc, transaction) => {
            // Handle debit values
            let debitValue = 0;
            if (typeof transaction.debit === 'number') {
                debitValue = transaction.debit;
            } else if (typeof transaction.debit === 'string') {
                debitValue = parseFloat(transaction.debit.replace(/[^0-9.-]+/g, "")) || 0;
            }

            // Handle credit values
            let creditValue = 0;
            if (typeof transaction.credit === 'number') {
                creditValue = transaction.credit;
            } else if (typeof transaction.credit === 'string') {
                creditValue = parseFloat(transaction.credit.replace(/[^0-9.-]+/g, "")) || 0;
            }

            acc.totalDebit += debitValue;
            acc.totalCredit += creditValue;

            return acc;
        }, { totalDebit: 0, totalCredit: 0 });

        // Net balance = Total Debit - Total Credit
        totals.netBalance = totals.totalDebit - totals.totalCredit;

        return totals;
    };

    const calculateInvoiceAging = (transactions) => {
        const today = new Date();
        const aging = {
            aging15: 0,
            aging30: 0,
            aging60: 0,
            aging90: 0
        };

        // Find the most recent balance - assuming transactions are sorted by date
        let currentBalance = 0;
        if (transactions.length > 0) {
            // Handle balance whether it's a number or string (with currency format)
            if (typeof transactions[0].balance === 'number') {
                currentBalance = transactions[0].balance;
            } else if (typeof transactions[0].balance === 'string') {
                // Remove currency symbols and convert to number
                currentBalance = parseFloat(transactions[0].balance.replace(/[^0-9.-]+/g, ""));
            }
        }

        // If balance is zero or negative, no aging needed
        if (currentBalance <= 0 || isNaN(currentBalance)) {
            return [aging];
        }

        // Find the oldest unpaid invoice date
        const invoiceEntries = transactions.filter(t => {
            // Handle both number and string types for debit
            let debitValue = 0;
            if (typeof t.debit === 'number') {
                debitValue = t.debit;
            } else if (typeof t.debit === 'string') {
                debitValue = parseFloat(t.debit.replace(/[^0-9.-]+/g, ""));
            }
            return debitValue > 0;
        });

        if (invoiceEntries.length === 0) {
            return [aging]; // No invoice entries found
        }

        // Sort by date (oldest first) - if not already sorted
        invoiceEntries.sort((a, b) => new Date(a.date) - new Date(b.date));

        // Get the oldest invoice date that might contribute to the current balance
        const oldestInvoiceDate = new Date(invoiceEntries[0].date);

        // Calculate days since the oldest invoice
        const daysOverdue = Math.floor((today - oldestInvoiceDate) / (1000 * 60 * 60 * 24));

        // Put the entire current balance in the appropriate aging bucket
        if (daysOverdue <= 15) {
            aging.aging15 = currentBalance;
        } else if (daysOverdue <= 30) {
            aging.aging30 = currentBalance;
        } else if (daysOverdue <= 60) {
            aging.aging60 = currentBalance;
        } else {
            aging.aging90 = currentBalance;
        }

        return [aging];
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatCurrency = (amount, currencyCode = 'KES') => {
        if (typeof amount !== 'number') return `${currencyCode} 0.00`;
        return `${currencyCode} ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };
    const exportCSV = () => {
        // Get current date for report header
        const currentDate = formatDate(new Date());

        // Create header section
        let csvContent = "STATEMENT OF ACCOUNT\n";
        csvContent += `Report Date: ${currentDate}\n\n`;

        // Transaction data
        const headers = ["Date", "Transaction Ref", "Details", "Type", "Debit (KES)", "Credit (KES)", "Running Balance (KES)"];
        const rows = transactions.map(t => [
            formatDate(t.date),
            t.transactionRef,
            t.details,
            t.invoiceType,
            formatNumberForCSV(t.debit),
            formatNumberForCSV(t.credit),
            formatNumberForCSV(t.balance)
        ]);

        // Helper function to format numbers without currency symbols for CSV
        function formatNumberForCSV(value) {
            if (!value) return "";
            if (typeof value === 'number') return value.toFixed(2);
            if (typeof value === 'string') {
                // Remove currency symbol and format
                const num = parseFloat(value.replace(/[^0-9.-]+/g, ""));
                return isNaN(num) ? value : num.toFixed(2);
            }
            return value;
        }

        // Format and escape CSV cells
        const escapeForCSV = (value) => {
            if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
                return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
        };

        csvContent += [headers, ...rows].map(row => row.map(escapeForCSV).join(",")).join("\n");

        // Add summary section
        const totalDebit = transactions.reduce((sum, t) => {
            const value = typeof t.debit === 'number' ? t.debit :
                (typeof t.debit === 'string' ? parseFloat(t.debit.replace(/[^0-9.-]+/g, "")) || 0 : 0);
            return sum + value;
        }, 0);

        const totalCredit = transactions.reduce((sum, t) => {
            const value = typeof t.credit === 'number' ? t.credit :
                (typeof t.credit === 'string' ? parseFloat(t.credit.replace(/[^0-9.-]+/g, "")) || 0 : 0);
            return sum + value;
        }, 0);

        // Get final balance from most recent transaction
        const finalBalance = transactions.length > 0 ?
            (typeof transactions[0].balance === 'number' ? transactions[0].balance :
                parseFloat(String(transactions[0].balance).replace(/[^0-9.-]+/g, "")) || 0) : 0;

        csvContent += "\n\nSUMMARY\n";
        csvContent += `Total Invoiced,${totalDebit.toFixed(2)}\n`;
        csvContent += `Total Payments,${totalCredit.toFixed(2)}\n`;
        csvContent += `Current Balance,${finalBalance.toFixed(2)}\n\n`;

        // Add aging data
        csvContent += "AGING SUMMARY\n";
        csvContent += "15 Days,30 Days,60 Days,90 Days,Total Outstanding\n";
        agingData.forEach(a => {
            const total = (a.aging15 || 0) + (a.aging30 || 0) + (a.aging60 || 0) + (a.aging90 || 0);
            csvContent += [
                formatNumberForCSV(a.aging15) || "0.00",
                formatNumberForCSV(a.aging30) || "0.00",
                formatNumberForCSV(a.aging60) || "0.00",
                formatNumberForCSV(a.aging90) || "0.00",
                formatNumberForCSV(total) || "0.00"
            ].join(",") + "\n";
        });

        // Add footer
        csvContent += "\nThank you for your business.";
        csvContent += "\nFor any questions regarding this statement, please contact billing@example.com";

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.setAttribute("download", `statement_of_account_${currentDate.replace(/\//g, '-')}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const formatCurrencyForPDF = (value) => {
        if (!value) return "";
        let num;
        if (typeof value === 'number') {
            num = value;
        } else if (typeof value === 'string') {
            num = parseFloat(value.replace(/[^0-9.-]+/g, ""));
            if (isNaN(num)) return value;
        } else {
            return "";
        }
        return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const exportPDF = () => {
        const doc = new jsPDF();
        const currentDate = formatDate(new Date());

        // Document title
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 78, 152);
        doc.text("STATEMENT OF ACCOUNT", 14, 25);

        // Document date
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
        doc.text(`Date: ${currentDate}`, 14, 35);

        // Account summary - right aligned
        const finalBalance = transactions.length > 0 ?
            (typeof transactions[0].balance === 'number' ? transactions[0].balance :
                parseFloat(String(transactions[0].balance).replace(/[^0-9.-]+/g, "")) || 0) : 0;

        doc.setFont('helvetica', 'bold');
        doc.text(
            `Current Balance: KES ${finalBalance.toLocaleString('en-US', {
                minimumFractionDigits: 2, maximumFractionDigits: 2
            })}`,
            195,
            35,
            { align: 'right' }
        );
        doc.setFont('helvetica', 'normal');

        const headers = [["Date", "Transaction Ref", "Details", "Debit (KES)", "Credit (KES)", "Balance (KES)"]];
        const data = transactions.map(t => [
            formatDate(t.date),
            t.transactionRef,
            t.details,
            formatCurrencyForPDF(t.debit) || "",
            formatCurrencyForPDF(t.credit) || "",
            formatCurrencyForPDF(t.balance)
        ]);

        // Draw transaction table
        autoTable(doc, {
            startY: 45,
            head: headers,
            body: data,
            styles: { fontSize: 9 },
            headStyles: {
                fillColor: [0, 78, 152],
                fontSize: 9,
                fontStyle: 'bold',
                halign: 'center'
            },
            columnStyles: {
                0: { cellWidth: 25 },
                1: { cellWidth: 30 },
                2: { cellWidth: 50 },
                3: { cellWidth: 25, halign: 'right' },
                4: { cellWidth: 25, halign: 'right' },
                5: { cellWidth: 25, halign: 'right' }
            },
            alternateRowStyles: { fillColor: [240, 240, 240] },
            margin: { top: 45 }
        });

        // Get Y position after transaction table
        const finalY = doc.lastAutoTable.finalY + 10;

        // Totals
        const totalDebit = transactions.reduce((sum, t) => {
            const value = typeof t.debit === 'number' ? t.debit :
                (typeof t.debit === 'string' ? parseFloat(t.debit.replace(/[^0-9.-]+/g, "")) || 0 : 0);
            return sum + value;
        }, 0);

        const totalCredit = transactions.reduce((sum, t) => {
            const value = typeof t.credit === 'number' ? t.credit :
                (typeof t.credit === 'string' ? parseFloat(t.credit.replace(/[^0-9.-]+/g, "")) || 0 : 0);
            return sum + value;
        }, 0);

        const summaryData = [
            ["Total Invoiced:", `KES ${formatCurrencyForPDF(totalDebit)}`],
            ["Total Payments:", `KES ${formatCurrencyForPDF(totalCredit)}`],
            ["Current Balance:", `KES ${formatCurrencyForPDF(finalBalance)}`]
        ];

        autoTable(doc, {
            startY: finalY,
            body: summaryData,
            theme: 'plain',
            styles: { fontSize: 10 },
            columnStyles: {
                0: { cellWidth: 100, fontStyle: 'bold', textColor: [0, 78, 152] },
                1: { cellWidth: 50, halign: 'right', fontStyle: 'bold', textColor: [0, 78, 152] }
            },
            margin: { left: 100 }
        });

        const afterSummaryY = doc.lastAutoTable.finalY + 10;

        // Aging Summary title
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 78, 152);
        doc.text("Aging Summary", 14, afterSummaryY);

        // Aging Table
        const agingHeaders = [["15 Days", "30 Days", "60 Days", "90+ Days", "Total Outstanding"]];
        const agingRows = agingData.map(a => {
            const total = (a.aging15 || 0) + (a.aging30 || 0) + (a.aging60 || 0) + (a.aging90 || 0);
            return [
                formatCurrencyForPDF(a.aging15) || "0.00",
                formatCurrencyForPDF(a.aging30) || "0.00",
                formatCurrencyForPDF(a.aging60) || "0.00",
                formatCurrencyForPDF(a.aging90) || "0.00",
                formatCurrencyForPDF(total) || "0.00"
            ];
        });

        autoTable(doc, {
            startY: afterSummaryY + 5,
            head: agingHeaders,
            body: agingRows,
            styles: { fontSize: 9, halign: 'right' },
            headStyles: {
                fillColor: [0, 78, 152],
                fontStyle: 'bold',
                halign: 'center'
            },
            theme: 'grid',
            alternateRowStyles: { fillColor: [240, 240, 240] }
        });

        // Footer on every page
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.setTextColor(100, 100, 100);
            doc.text(
                `Thank you for your business. For any questions regarding this statement, please contact billing@example.com`,
                doc.internal.pageSize.getWidth() / 2,
                doc.internal.pageSize.getHeight() - 10,
                { align: 'center' }
            );
            doc.text(
                `Page ${i} of ${pageCount}`,
                doc.internal.pageSize.getWidth() / 2,
                doc.internal.pageSize.getHeight() - 5,
                { align: 'center' }
            );
        }

        doc.save(`statement_of_account_${currentDate.replace(/\//g, '-')}.pdf`);
    };

    const exportXLSX = () => {
        const currentDate = formatDate(new Date());

        // Format numbers without currency symbols for Excel
        const formatNumberForExcel = (value) => {
            if (!value) return "";
            if (typeof value === 'number') return value;
            if (typeof value === 'string') {
                // Remove currency symbol and convert to number
                const num = parseFloat(value.replace(/[^0-9.-]+/g, ""));
                return isNaN(num) ? value : num;
            }
            return value;
        };

        // Calculate totals for summary
        const totalDebit = transactions.reduce((sum, t) => {
            const value = typeof t.debit === 'number' ? t.debit :
                (typeof t.debit === 'string' ? parseFloat(t.debit.replace(/[^0-9.-]+/g, "")) || 0 : 0);
            return sum + value;
        }, 0);

        const totalCredit = transactions.reduce((sum, t) => {
            const value = typeof t.credit === 'number' ? t.credit :
                (typeof t.credit === 'string' ? parseFloat(t.credit.replace(/[^0-9.-]+/g, "")) || 0 : 0);
            return sum + value;
        }, 0);

        // Get final balance from most recent transaction
        const finalBalance = transactions.length > 0 ?
            (typeof transactions[0].balance === 'number' ? transactions[0].balance :
                parseFloat(String(transactions[0].balance).replace(/[^0-9.-]+/g, "")) || 0) : 0;

        // Create header data
        const headerData = [
            ["STATEMENT OF ACCOUNT"],
            [""],
            ["Report Date:", currentDate],
            [""],
            [""]
        ];

        // Transaction data with headers
        const transactionData = [
            ["Date", "Transaction Ref", "Details", "Type", "Debit", "Credit", "Balance"]
        ];
        // Add actual transaction data
        transactions.forEach(t => {
            transactionData.push([
                formatDate(t.date),
                t.transactionRef,
                t.details,
                t.invoiceType,
                formatNumberForExcel(t.debit),
                formatNumberForExcel(t.credit),
                formatNumberForExcel(t.balance)
            ]);
        });

        // Summary data
        const summaryData = [
            [""],
            ["SUMMARY"],
            ["Total Invoiced", totalDebit],
            ["Total Payments", totalCredit],
            ["Current Balance", finalBalance],
            [""]
        ];

        // Aging data with headers
        const agingHeadersAndData = [
            ["AGING SUMMARY"],
            ["15 Days", "30 Days", "60 Days", "90+ Days", "Total Outstanding"]
        ];

        // Add actual aging data - Fixed to properly handle the aging data
        agingData.forEach(a => {
            const aging15 = formatNumberForExcel(a.aging15) || 0;
            const aging30 = formatNumberForExcel(a.aging30) || 0;
            const aging60 = formatNumberForExcel(a.aging60) || 0;
            const aging90 = formatNumberForExcel(a.aging90) || 0;
            const total = aging15 + aging30 + aging60 + aging90;

            agingHeadersAndData.push([
                aging15,
                aging30,
                aging60,
                aging90,
                total
            ]);
        });

        // Combine all data for the main sheet
        const allData = [
            ...headerData,
            ...transactionData,
            [""],
            ...summaryData,
            [""],
            ...agingHeadersAndData
        ];

        // Create workbook and sheets
        const wb = XLSX.utils.book_new();

        // Convert to worksheet format
        const ws = XLSX.utils.aoa_to_sheet(allData);

        // Set column widths
        const colWidths = [
            { wch: 15 },  // A - Date
            { wch: 20 },  // B - Transaction Ref
            { wch: 40 },  // C - Details
            { wch: 15 },  // D - Type
            { wch: 15 },  // E - Debit
            { wch: 15 },  // F - Credit
            { wch: 15 }   // G - Balance
        ];
        ws['!cols'] = colWidths;

        // Add the sheet to the workbook
        XLSX.utils.book_append_sheet(wb, ws, "Statement of Account");

        // Save the file
        XLSX.writeFile(wb, `statement_of_account_${currentDate.replace(/\//g, '-')}.xlsx`);
    };

    useEffect(() => {
        getUnit();
    }, [])

    // Add this to useEffect to refetch when tenantId changes
    useEffect(() => {
        const loadStatementData = async () => {
            if (tenantId) {
                const facilityId = await getItem("selectedFacilityId");
                fetchStatementOfAccounts(facilityId, tenantId);
            }
        };

        loadStatementData();
    }, [tenantId]);


    useEffect(() => {
        if (transactions && transactions.length > 0) {
            const agingData = calculateInvoiceAging(transactions);
            setAgingData(agingData);
        } else {
            setAgingData([]);
        }
    }, [transactions]);


    return (
        <Layout>
            <div className="page-header">
                <div className="page-block">
                    <div className="row align-items-center">
                        <div className="col-md-12">
                            <ul className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <Link to={"#"}>Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to={"#"}>Occupancy Reports</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to={"#"}>View Unit</Link>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12">
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card">
                                <div className="card-header">
                                    <Link to={"#"} onClick={() => navigate(-1)}><span><i className="ti ti-arrow-narrow-left"></i> Back</span></Link>
                                </div>
                            </div>
                            <div className="card">
                                <div className="card-body py-0">
                                    <ul className="nav nav-tabs profile-tabs" id="myTab" role="tablist">
                                        <li className="nav-item">
                                            <a className="nav-link active" id="profile-tab-1" data-bs-toggle="tab" href="#profile-1" role="tab" aria-selected="true">
                                                <i className="ti ti-building-bank me-2"></i>Info
                                            </a>
                                        </li>
                                        <li className="nav-item">
                                            <a className="nav-link" id="profile-tab-2" data-bs-toggle="tab" href="#profile-2" role="tab" aria-selected="true">
                                                <i className="ti ti-building-warehouse me-2"></i>Lease Income
                                            </a>
                                        </li>
                                        <li className="nav-item">
                                            <a className="nav-link" id="profile-tab-3" data-bs-toggle="tab" href="#profile-3" role="tab" aria-selected="true">
                                                <i className="ti ti-olympics me-2"></i>Statements
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <div className="tab-content">
                                <div className="tab-pane show active" id="profile-1" role="tabpanel" aria-labelledby="profile-tab-1">
                                    <div className="card">
                                        <div className="card-header">
                                            <h5>Unit Details</h5>
                                        </div>

                                        <div className='card-body'>
                                            <div className="row">

                                                <div className="col-lg-12 col-xxl-12">
                                                    <div className="card">
                                                        <div className="card-body position-relative">
                                                            <div className="container mt-4">

                                                                <div className="row">
                                                                    {/* Unit Details */}
                                                                    <div className="col-md-6">
                                                                        <div className="mb-4 p-3 border rounded shadow-sm bg-white">
                                                                            <h3 className="mb-3">Unit Information</h3>
                                                                            <p><strong>Unit Name:</strong> {unit.name}</p>
                                                                            <p><strong>Unit Type:</strong> {unit.unitType}</p>
                                                                            <p><strong>Floor Number:</strong> {unit.floorUnitNo}</p>
                                                                            <p>
                                                                                <strong>Status:</strong>{' '}
                                                                                {unit.tenant ? (
                                                                                    <Tag severity="success" value="Occupied by Tenant" />
                                                                                ) : unit.homeOwner ? (
                                                                                    <Tag severity="info" value="Occupied by Homeowner" />
                                                                                ) : (
                                                                                    <Tag severity="danger" value="Vacant" />
                                                                                )}
                                                                            </p>
                                                                        </div>
                                                                    </div>

                                                                    <div className="col-md-6">
                                                                        <div className="mb-4 p-3 border rounded shadow-sm bg-white">
                                                                            <h3 className="mb-3">Tenant Information</h3>
                                                                            {tenant ? (
                                                                                <>
                                                                                    <p><strong>Name:</strong> {tenant.fullName || "N/A"}</p>
                                                                                    <p><strong>Contact:</strong> 0{tenant.phoneNumber || "N/A"}</p>
                                                                                </>
                                                                            ) : (
                                                                                <p>No Active Tenant</p>
                                                                            )}
                                                                        </div>
                                                                    </div>

                                                                </div>

                                                            </div>

                                                        </div>
                                                    </div>

                                                </div>

                                            </div>
                                        </div>

                                    </div>
                                </div>

                                <div className="tab-pane fade" id="profile-2" role="tabpanel" aria-labelledby="profile-tab-2">
                                    <div className="card">
                                        <div className="card-header">
                                            <h5>Income by Lease</h5>
                                        </div>

                                        <div className='card-body'>
                                            <div className="row">

                                                <div className="col-lg-12 col-xxl-12">
                                                    <div className="card">
                                                        <div className="card-body position-relative">
                                                            <div className="container">
                                                                {/* Income Summary */}
                                                                <div className="row mb-3">
                                                                    <div className="col-md-4">
                                                                        <div className="p-3 border rounded bg-light text-center">
                                                                            <h5>Total Income</h5>
                                                                            <h4 className="text-success">Ksh {leaseIncome?.totalIncome || 0}</h4>
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-md-4">
                                                                        <div className="p-3 border rounded bg-light text-center">
                                                                            <h5>Total Paid</h5>
                                                                            <h4 className="text-primary">Ksh {leaseIncome?.totalPaid || 0}</h4>
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-md-4">
                                                                        <div className="p-3 border rounded bg-light text-center">
                                                                            <h5>Balance</h5>
                                                                            <h4 className="text-danger">Ksh {leaseIncome?.totalBalance || 0}</h4>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Invoices List */}
                                                                <div className="row">
                                                                    <div className="col-12">
                                                                        <h5>Lease Invoices</h5>
                                                                        <table className="table table-striped">
                                                                            <thead>
                                                                                <tr>
                                                                                    <th>#</th>
                                                                                    <th>Invoice No</th>
                                                                                    <th>Amount</th>
                                                                                    <th>Paid</th>
                                                                                    <th>Balance</th>
                                                                                    <th>Status</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {leaseIncome?.invoices?.length > 0 ? (
                                                                                    leaseIncome.invoices.map((invoice, index) => (
                                                                                        <tr key={invoice._id}>
                                                                                            <td>{index + 1}</td>
                                                                                            <td>{invoice.invoiceNumber}</td>
                                                                                            <td>Ksh {invoice.totalAmount}</td>
                                                                                            <td className="text-primary">Ksh {invoice.amountPaid}</td>
                                                                                            <td className="text-danger">Ksh {invoice.totalAmount - invoice.amountPaid}</td>
                                                                                            <td>{invoice.status}</td>
                                                                                        </tr>
                                                                                    ))
                                                                                ) : (
                                                                                    <tr>
                                                                                        <td colSpan="5" className="text-center text-muted">
                                                                                            No lease invoices found.
                                                                                        </td>
                                                                                    </tr>
                                                                                )}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                        </div>
                                                    </div>

                                                </div>

                                            </div>
                                        </div>

                                    </div>
                                </div>

                                <div className="tab-pane fade" id="profile-3" role="tabpanel" aria-labelledby="profile-tab-3">
                                    <div className="card">

                                        <div className="card-header d-flex justify-content-between align-items-start">
                                            <h5 className="m-0">Transactions</h5>

                                            <div className="d-flex">
                                                <Button type="button" label="CSV" rounded onClick={exportCSV} data-pr-tooltip="CSV" />
                                                <Button type="button" label="XLS" severity="success" rounded onClick={exportXLSX} data-pr-tooltip="XLS" />
                                                <Button type="button" label="PDF" severity="warning" rounded onClick={exportPDF} data-pr-tooltip="PDF" />
                                            </div>
                                        </div>




                                        <div className='card-body'>
                                            <div className="row">

                                                <div className="col-12">
                                                    <div className="mb-4 p-3 border rounded-1 shadow-sm bg-white">
                                                        <h4 className="mb-3">Transactions</h4>
                                                        <div className='table-responsive'>

                                                            {/* <DataTable
                                                                value={transactions}
                                                                emptyMessage="No transactions found."
                                                                sortMode="multiple"
                                                                paginator
                                                                rows={10}
                                                                stripedRows
                                                                className="shadow-md rounded-lg overflow-hidden"
                                                                rowClassName={(data) => {
                                                                    // Apply different styling based on transaction type
                                                                    if (data.isTenantTransaction) return "bg-yellow-50 hover:bg-yellow-100 transition-colors";
                                                                    return data.invoiceType === 'Contract'
                                                                        ? "bg-blue-50 hover:bg-blue-100 transition-colors"
                                                                        : "hover:bg-gray-50 transition-colors";
                                                                }}
                                                                footer={<div className="p-3 text-sm text-gray-500">Showing {transactions?.length || 0} transactions</div>}
                                                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                                                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} transactions"
                                                                rowsPerPageOptions={[10, 15, 25]}
                                                            >
                                                                <Column
                                                                    field="date"
                                                                    header="Date"
                                                                    body={(rowData) => formatDate(rowData.date)}
                                                                    headerClassName="bg-blue-100 font-bold"
                                                                    sortable
                                                                />
                                                                <Column
                                                                    field="transactionRef"
                                                                    header="Transaction Ref"
                                                                    headerClassName="bg-blue-100 font-bold"
                                                                    sortable
                                                                />
                                                                <Column
                                                                    field="details"
                                                                    header="Details"
                                                                    headerClassName="bg-blue-100 font-bold"
                                                                    body={(rowData) => (
                                                                        <div className="flex items-center">
                                                                            {rowData.invoiceType === 'Contract' && (
                                                                                <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                                                                            )}
                                                                            {rowData.invoiceType === 'Lease' && !rowData.isTenantTransaction && (
                                                                                <span className="inline-block w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                                                                            )}
                                                                            {rowData.isTenantTransaction && (
                                                                                <span className="inline-block w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                                                                            )}
                                                                            {rowData.details}
                                                                        </div>
                                                                    )}
                                                                    sortable
                                                                />
                                                                <Column
                                                                    field="invoiceType"
                                                                    header="Type"
                                                                    headerClassName="bg-blue-100 font-bold"
                                                                    body={(rowData) => (
                                                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${rowData.invoiceType === 'Contract'
                                                                            ? 'bg-blue-100 text-blue-800'
                                                                            : 'bg-green-100 text-green-800'
                                                                            }`}>
                                                                            {rowData.invoiceType}
                                                                        </span>
                                                                    )}
                                                                    sortable
                                                                />
                                                                <Column
                                                                    field="debit"
                                                                    header="Debit"
                                                                    headerClassName="bg-blue-100 font-bold"
                                                                    body={(rowData) => rowData.debit && (
                                                                        <span className="text-red-600 font-medium">
                                                                            {typeof rowData.debit === 'number' ?
                                                                                new Intl.NumberFormat('en-US', { style: 'currency', currency: 'KES' }).format(rowData.debit) :
                                                                                rowData.debit}
                                                                        </span>
                                                                    )}
                                                                    sortable
                                                                />
                                                                <Column
                                                                    field="credit"
                                                                    header="Credit"
                                                                    headerClassName="bg-blue-100 font-bold"
                                                                    body={(rowData) => rowData.credit && (
                                                                        <span className="text-green-600 font-medium">
                                                                            {typeof rowData.credit === 'number' ?
                                                                                new Intl.NumberFormat('en-US', { style: 'currency', currency: 'KES' }).format(rowData.credit) :
                                                                                rowData.credit}
                                                                        </span>
                                                                    )}
                                                                    sortable
                                                                />
                                                                <Column
                                                                    field="balance"
                                                                    header="Balance"
                                                                    headerClassName="bg-blue-100 font-bold"
                                                                    body={(rowData) => (
                                                                        <span className={`font-medium ${parseFloat(rowData.balance) < 0 ? 'text-red-600' : 'text-blue-600'}`}>
                                                                            {typeof rowData.balance === 'number' ?
                                                                                new Intl.NumberFormat('en-US', { style: 'currency', currency: 'KES' }).format(rowData.balance) :
                                                                                rowData.balance}
                                                                        </span>
                                                                    )}
                                                                    sortable
                                                                />
                                                            </DataTable> */}

                                                            <DataTable
                                                                value={transactions}
                                                                emptyMessage="No transactions found."
                                                                sortMode="multiple"
                                                                paginator
                                                                rows={10}
                                                                stripedRows
                                                                className="shadow-md rounded-lg overflow-hidden"
                                                                rowClassName={(data) => {
                                                                    // Apply different styling based on transaction type
                                                                    if (data.isTenantTransaction) return "bg-yellow-50 hover:bg-yellow-100 transition-colors";
                                                                    return data.invoiceType === 'Contract'
                                                                        ? "bg-blue-50 hover:bg-blue-100 transition-colors"
                                                                        : "hover:bg-gray-50 transition-colors";
                                                                }}
                                                                footer={
                                                                    <div className="p-3 bg-gray-50 border-t">
                                                                        <div className="text-sm text-gray-500 mb-2">
                                                                            Showing {transactions?.length || 0} transactions
                                                                        </div>
                                                                        <div className="row fw-bold">
                                                                            <div className="col">
                                                                                <span className="text-muted">Totals:</span>
                                                                            </div>
                                                                            <div className="col text-end">
                                                                                <span className="text-red-600">
                                                                                    Debit: {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'KES' }).format(
                                                                                        transactions?.reduce((sum, t) => sum + (parseFloat(t.debit) || 0), 0) || 0
                                                                                    )}
                                                                                </span>
                                                                            </div>
                                                                            <div className="col text-end">
                                                                                <span className="text-green-600">
                                                                                    Credit: {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'KES' }).format(
                                                                                        transactions?.reduce((sum, t) => sum + (parseFloat(t.credit) || 0), 0) || 0
                                                                                    )}
                                                                                </span>
                                                                            </div>
                                                                            <div className="col text-end">
                                                                                <span className="text-blue-600">
                                                                                    Net Balance: {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'KES' }).format(
                                                                                        (transactions?.reduce((sum, t) => sum + (parseFloat(t.debit) || 0), 0) || 0) -
                                                                                        (transactions?.reduce((sum, t) => sum + (parseFloat(t.credit) || 0), 0) || 0)
                                                                                    )}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                }
                                                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                                                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} transactions"
                                                                rowsPerPageOptions={[10, 15, 25]}
                                                            >
                                                                <Column
                                                                    field="date"
                                                                    header="Date"
                                                                    body={(rowData) => formatDate(rowData.date)}
                                                                    headerClassName="bg-blue-100 font-bold"
                                                                    sortable
                                                                />
                                                                <Column
                                                                    field="transactionRef"
                                                                    header="Transaction Ref"
                                                                    headerClassName="bg-blue-100 font-bold"
                                                                    sortable
                                                                />
                                                                <Column
                                                                    field="details"
                                                                    header="Details"
                                                                    headerClassName="bg-blue-100 font-bold"
                                                                    body={(rowData) => (
                                                                        <div className="flex items-center">
                                                                            {rowData.invoiceType === 'Contract' && (
                                                                                <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                                                                            )}
                                                                            {rowData.invoiceType === 'Lease' && !rowData.isTenantTransaction && (
                                                                                <span className="inline-block w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                                                                            )}
                                                                            {rowData.isTenantTransaction && (
                                                                                <span className="inline-block w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                                                                            )}
                                                                            {rowData.details}
                                                                        </div>
                                                                    )}
                                                                    sortable
                                                                />
                                                                <Column
                                                                    field="invoiceType"
                                                                    header="Type"
                                                                    headerClassName="bg-blue-100 font-bold"
                                                                    body={(rowData) => (
                                                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${rowData.invoiceType === 'Contract'
                                                                                ? 'bg-blue-100 text-blue-800'
                                                                                : 'bg-green-100 text-green-800'
                                                                            }`}>
                                                                            {rowData.invoiceType}
                                                                        </span>
                                                                    )}
                                                                    sortable
                                                                />
                                                                <Column
                                                                    field="debit"
                                                                    header="Debit"
                                                                    headerClassName="bg-blue-100 font-bold"
                                                                    body={(rowData) => rowData.debit && (
                                                                        <span className="text-red-600 font-medium">
                                                                            {typeof rowData.debit === 'number' ?
                                                                                new Intl.NumberFormat('en-US', { style: 'currency', currency: 'KES' }).format(rowData.debit) :
                                                                                rowData.debit}
                                                                        </span>
                                                                    )}
                                                                    sortable
                                                                />
                                                                <Column
                                                                    field="credit"
                                                                    header="Credit"
                                                                    headerClassName="bg-blue-100 font-bold"
                                                                    body={(rowData) => rowData.credit && (
                                                                        <span className="text-green-600 font-medium">
                                                                            {typeof rowData.credit === 'number' ?
                                                                                new Intl.NumberFormat('en-US', { style: 'currency', currency: 'KES' }).format(rowData.credit) :
                                                                                rowData.credit}
                                                                        </span>
                                                                    )}
                                                                    sortable
                                                                />
                                                                <Column
                                                                    field="balance"
                                                                    header="Balance"
                                                                    headerClassName="bg-blue-100 font-bold"
                                                                    body={(rowData) => (
                                                                        <span className={`font-medium ${parseFloat(rowData.balance) < 0 ? 'text-red-600' : 'text-blue-600'}`}>
                                                                            {typeof rowData.balance === 'number' ?
                                                                                new Intl.NumberFormat('en-US', { style: 'currency', currency: 'KES' }).format(rowData.balance) :
                                                                                rowData.balance}
                                                                        </span>
                                                                    )}
                                                                    sortable
                                                                />
                                                            </DataTable>

                                                        </div>
                                                    </div>

                                                </div>

                                                <div className="col-12">
                                                    <div className="mb-4 p-3 border rounded-1 shadow-sm bg-white">
                                                        <h4 className="mb-3">Overdue Aging Summary</h4>
                                                        <div className='table-responsive'>
                                                            <DataTable
                                                                value={agingData}
                                                                emptyMessage="No overdue invoices."
                                                                tableStyle={{ minWidth: '40rem' }}
                                                            >
                                                                <Column field="aging15" header="15 Days" body={(rowData) => {
                                                                    const amount = rowData.aging15;
                                                                    return (
                                                                        <span className={amount > 0 ? 'text-red-500' : ''}>
                                                                            {formatCurrency(amount)}
                                                                        </span>
                                                                    );
                                                                }}
                                                                    headerClassName="bg-blue-100 font-bold" />
                                                                <Column field="aging30" header="30 Days" body={(rowData) => {
                                                                    const amount = rowData.aging30;
                                                                    return (
                                                                        <span className={amount > 0 ? 'text-red-500' : ''}>
                                                                            {formatCurrency(amount)}
                                                                        </span>
                                                                    );
                                                                }}
                                                                    headerClassName="bg-blue-100 font-bold" />
                                                                <Column field="aging60" header="60 Days" body={(rowData) => {
                                                                    const amount = rowData.aging60;
                                                                    return (
                                                                        <span className={amount > 0 ? 'text-red-500' : ''}>
                                                                            {formatCurrency(amount)}
                                                                        </span>
                                                                    );
                                                                }}
                                                                    headerClassName="bg-blue-100 font-bold" />
                                                                <Column field="aging90" header="90 Days" body={(rowData) => {
                                                                    const amount = rowData.aging90;
                                                                    return (
                                                                        <span className={amount > 0 ? 'text-red-500' : ''}>
                                                                            {formatCurrency(amount)}
                                                                        </span>
                                                                    );
                                                                }}
                                                                    headerClassName="bg-blue-100 font-bold" />
                                                            </DataTable>
                                                        </div>
                                                    </div>
                                                </div>

                                            </div>
                                        </div>
                                    </div>
                                </div>

                            </div>

                        </div>
                    </div>
                </div>
            </div>

            <Dialog
                header="Lease Income"
                visible={viewIncomeDialog}
                style={{ width: '50vw' }}
                onHide={() => setViewIncomeDialog(false)}
            >
                <div className="container">
                    {/* Income Summary */}
                    <div className="row mb-3">
                        <div className="col-md-4">
                            <div className="p-3 border rounded bg-light text-center">
                                <h5>Total Income</h5>
                                <h4 className="text-success">Ksh {leaseIncome?.totalIncome || 0}</h4>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="p-3 border rounded bg-light text-center">
                                <h5>Total Paid</h5>
                                <h4 className="text-primary">Ksh {leaseIncome?.totalPaid || 0}</h4>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="p-3 border rounded bg-light text-center">
                                <h5>Balance</h5>
                                <h4 className="text-danger">Ksh {leaseIncome?.totalBalance || 0}</h4>
                            </div>
                        </div>
                    </div>

                    {/* Invoices List */}
                    <div className="row">
                        <div className="col-12">
                            <h5>Lease Invoices</h5>
                            <table className="table table-striped">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Invoice No</th>
                                        <th>Amount</th>
                                        <th>Paid</th>
                                        <th>Balance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {leaseIncome?.invoices?.length > 0 ? (
                                        leaseIncome.invoices.map((invoice, index) => (
                                            <tr key={invoice._id}>
                                                <td>{index + 1}</td>
                                                <td>{invoice.invoiceNumber}</td>
                                                <td>Ksh {invoice.totalAmount}</td>
                                                <td className="text-primary">Ksh {invoice.amountPaid}</td>
                                                <td className="text-danger">Ksh {invoice.totalAmount - invoice.amountPaid}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="text-center text-muted">
                                                No lease invoices found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </Dialog>

        </Layout>
    )
}

export default ViewUnit